// src/views/scannedData/ScannedData.js
import React, { useState } from "react";
import PropTypes from "prop-types";
import { database } from "../../firebase";
import { ref, runTransaction } from "firebase/database";
import { getAuth, signOut } from "firebase/auth";

import useScans from "./useScans";
import ScanRow from "./ScanRow";
import SelectedDataDisplay from "./SelectedDataDisplay";

import "./ScannedData.css";

export default function ScannedData({
  user,
  viewUserID,
  isAdmin,
  onStartScan,
  onOpenPhotoCapture,
  onOpenAddressEditor,
  onBackToUserSelection,
}) {
  const scans = useScans(user, viewUserID);
  const [selectedData, setSelectedData] = useState(null);
  /* New 3-state status: 'verified', 'disputed', 'rejected' (or null) */
  /* Filter options: 'all', 'verified', 'disputed', 'rejected' */
  const [filterStatus, setFilterStatus] = useState("all");

  /* Modal State for Status Updates */
  const [statusModal, setStatusModal] = useState({
    open: false,
    scan: null,
    targetStatus: "",
    note: ""
  });

  const userPath = viewUserID || user?.email?.replaceAll(".", "-");

  // Determine if admin is viewing another user's data
  const isViewingOtherUser = isAdmin && viewUserID && viewUserID !== user?.email?.replaceAll(".", "-");

  const handleRequestUpload = async (ev, scan) => {
    ev.stopPropagation();
    if (!userPath) return;

    const requestRef = ref(
      database,
      `data/scans/${userPath}/${scan.key}/requestToUpload`
    );

    await runTransaction(requestRef, (v) =>
      Number.isNaN(Number(v)) ? 1 : Number(v) + 1
    );

    onOpenPhotoCapture?.(userPath, scan.key);
  };

  const handleEditAddress = (ev, scan) => {
    ev.stopPropagation();
    if (!userPath) return;
    onOpenAddressEditor?.(userPath, scan.key);
  };

  /* Initiate Status Update - Open Modal */
  const handleInitiateStatusUpdate = (ev, scan, newStatus) => {
    ev.stopPropagation();
    // If the admin is just clicking the same status, we might want to let them edit the note.
    // Or if it's a new status.
    // Use existing note if available.
    setStatusModal({
      open: true,
      scan: scan,
      targetStatus: newStatus,
      note: scan.note || ""
    });
  };

  const handleConfirmStatusUpdate = async () => {
    const { scan, targetStatus, note } = statusModal;
    if (!userPath || !scan) return;

    // Validation
    // if has disputed or rejected the row, then providing a note is compulsory
    if ((targetStatus === 'disputed' || targetStatus === 'rejected') && !note.trim()) {
      alert(`A note is required when marking as ${targetStatus}.`);
      return;
    }

    const scanRef = ref(database, `data/scans/${userPath}/${scan.key}`);

    await runTransaction(scanRef, (currentData) => {
      if (currentData) {
        currentData.status = targetStatus;
        currentData.note = note.trim(); // Save the note
        return currentData;
      }
      return currentData;
    });

    setStatusModal({ open: false, scan: null, targetStatus: "", note: "" });
  };

  const handleCancelStatusUpdate = () => {
    setStatusModal({ open: false, scan: null, targetStatus: "", note: "" });
  };


  const handleSignOut = async () => {
    await signOut(getAuth());
    window.location.reload();
  };

  /* Helper to normalize status from scan data */
  const getScanStatus = (scan) => {
    if (scan.status) return scan.status; // 'verified', 'disputed', 'rejected'
    return "pending"; // or null
  };

  return (
    <div>
      <h2 id="scanned-data-title">Scanned Data</h2>

      <div id="button-container">
        <button id="add-scan-button" onClick={onStartScan}>
          Add Scan
        </button>
        <button id="signout-button" onClick={handleSignOut}>
          Sign Out
        </button>
      </div>

      <div className="filter-wrapper" style={{ textAlign: "center", marginBottom: "15px" }}>
        <div className="filter-inner" style={{ display: "inline-block" }}>
          {/* <span className="filter-label">Filter:</span> */}
          <div className="toggle-track">
            {["all", "verified", "disputed", "rejected"].map((stat) => (
              <button
                key={stat}
                onClick={() => setFilterStatus(stat)}
                className={`toggle-option ${filterStatus === stat ? "active" : ""}`}
                data-value={stat}
              >
                {stat.charAt(0).toUpperCase() + stat.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {
        isAdmin && viewUserID && (
          <div className="admin-mode-banner">
            <div className="admin-user-id">
              <strong>{viewUserID}</strong>
            </div>
            <button
              onClick={onBackToUserSelection}
              className="admin-back-button"
            >
              ← Back to User Selection
            </button>
          </div>
        )
      }

      <div className="table-wrapper">
        <table id="scanned-data-table">
          <thead>
            <tr>
              <th>Address</th>
              <th>Date</th>
              <th>Time</th>
              <th>Pic Taken</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {scans
              .filter((scan) => {
                if (filterStatus === "all") return true;
                const currentStatus = getScanStatus(scan);
                return currentStatus === filterStatus;
              })
              .map((scan) => (
                <ScanRow
                  key={scan.key}
                  scan={scan}
                  isSelected={selectedData?.key === scan.key}
                  onSelect={(scanToSelect) => {
                    setSelectedData((prev) =>
                      prev?.key === scanToSelect.key ? null : scanToSelect
                    );
                  }}
                  onRequestUpload={handleRequestUpload}
                  onEditAddress={handleEditAddress}
                  isAdmin={isAdmin}
                  isViewingOtherUser={isViewingOtherUser}
                  status={getScanStatus(scan)}
                  onUpdateStatus={handleInitiateStatusUpdate}
                />
              ))}
          </tbody>
        </table>
      </div>

      <SelectedDataDisplay selectedData={selectedData} />

      {/* Status Update Modal */}
      {statusModal.open && (
        <div className="status-modal-overlay">
          <div className="status-modal">
            <h3>Update Status: {statusModal.targetStatus}</h3>
            <p className="status-modal-subtitle">
              {statusModal.targetStatus === "verified" ? "Note is optional." : "Note is required."}
            </p>
            <textarea
              className="status-modal-note"
              placeholder="Enter a note..."
              value={statusModal.note}
              onChange={(e) => setStatusModal({ ...statusModal, note: e.target.value })}
            />
            <div className="status-modal-actions">
              <button className="status-modal-cancel" onClick={handleCancelStatusUpdate}>Cancel</button>
              <button className="status-modal-confirm" onClick={handleConfirmStatusUpdate}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div >
  );
}

ScannedData.propTypes = {
  user: PropTypes.object,
  viewUserID: PropTypes.string,
  isAdmin: PropTypes.bool,
  onStartScan: PropTypes.func,
  onOpenPhotoCapture: PropTypes.func,
  onOpenAddressEditor: PropTypes.func,
  onBackToUserSelection: PropTypes.func,
};
